import { Box, Divider, IconButton, makeStyles } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/HighlightOffRounded';
import { MediaType } from '@src/model/frontendmodel';
import { enqueueQuickMessageFiles } from '@src/reducers/quickMessages';
import { RootState } from '@src/store/reduxstore';
import React from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import closeIcon from './img/close.png';
import pdfIcon from './img/pdf.png';

const NAME_MAX_LENGTH = 9;
const PADDING_BOTTOM = 80;

const QuickMessaFilesPreview: React.FC<{
  anchorRef: React.MutableRefObject<HTMLDivElement | null>;
}> = ({ anchorRef }) => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const {
    root,
    content,
    contentTitle,
    contentItems,
    fileName,
    removeItem,
    closePreviewIcon,
    item,
  } = useStyles();

  const bottomPos = anchorRef.current?.getClientRects()[0]?.height || 0;

  const queueFiles = useSelector(
    (state: RootState) => state.quickMessages.queueFiles
  );

  const onRemoveValue = (index: number) => {
    dispatch(
      enqueueQuickMessageFiles([
        ...queueFiles.slice(0, index),
        ...queueFiles.slice(index + 1),
      ])
    );
  };

  const getFileName = (name: string) => {
    const [fName, fileExt] = (name.split('/').pop() || '')?.split('.');

    if (fName.length > NAME_MAX_LENGTH)
      return `${fName.slice(0, NAME_MAX_LENGTH - 3)}... ${fileExt}`;

    return `${fName}.${fileExt}`;
  };

  const handleClose = () => dispatch(enqueueQuickMessageFiles([]));

  if (queueFiles.length === 0) return <></>;

  return (
    <Box className={root} style={{ bottom: bottomPos + PADDING_BOTTOM }}>
      <Box>
        <Box className={content}>
          <Box>
            <Box className={contentTitle}>
              {formatMessage({
                id: 'ChatMessageComposer.quickMessagesFilesPreviewTitle',
              })}
            </Box>

            <IconButton
              className={closePreviewIcon}
              component="span"
              style={{ padding: 0 }}
              onClick={handleClose}
            >
              <CloseIcon fontSize="small" color="inherit" />
            </IconButton>
          </Box>

          <Divider />
          <Box className={contentItems}>
            {queueFiles.map(({ url, type }, index) => (
              <Box className={item} key={url}>
                <img
                  style={{
                    width: '65px',
                    height: '65px',
                    objectFit: 'contain',
                  }}
                  src={type === MediaType.IMAGE ? url : pdfIcon}
                  alt="img"
                />
                <Box
                  className={removeItem}
                  onClick={() => onRemoveValue(index)}
                >
                  <img src={closeIcon} alt="close" />
                </Box>
                <Box className={fileName}>{getFileName(url)}</Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    zIndex: 199,
    position: 'absolute',
    left: '14px',
    width: '96%',
    borderRadius: '8px',
    boxShadow: '0px 16px 24px 0px #0000003D',
    backgroundColor: theme.palette.grey[100],
  },
  content: {
    padding: '11px 16px',
    height: '155px',
    minWidth: '100%',
  },
  contentTitle: {
    fontSize: '14px',
    fontWeight: 600,
    paddingBottom: '10px',
  },
  contentItems: {
    display: 'flex',
    justifyContent: 'flex-start',
    gap: '15px',
    paddingTop: '14px',
    paddingBottom: '15px',
  },
  fileName: {
    fontSize: '10px',
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  item: {
    position: 'relative',
    width: '71px',
    height: '89px',
    textAlign: 'center',
  },
  removeItem: {
    position: 'absolute',
    top: '-8px',
    right: '0px',
    cursor: 'pointer',
    '& img': {
      width: '12px',
      height: '12px',
    },
  },
  closePreviewIcon: {
    position: 'absolute',
    top: '5px',
    right: '5px',
    color: theme.palette.grey[500],
  },
}));

export default QuickMessaFilesPreview;
